<!-- 퀼 에디터 래퍼 (2026-09-26)
     vue-quill-editor@3.0.6 를 대체한다. 그 패키지는 2019년 이후 방치됐고 퀼 1.x
     전용이라, 에디터의 진입점을 남의 죽은 코드에 두지 않으려고 직접 들고 온다.
     동작은 원본과 같게 맞췄다 — props·emit 이름, pasteHTML 로 값 넣기,
     _content 비교로 되먹임 막기까지 그대로다. -->
<template>
  <div class="quill-editor">
    <slot name="toolbar"></slot>
    <div ref="editor"></div>
  </div>
</template>

<script>
import Quill from 'quill'

// 원본(vue-quill-editor/src/options.js)의 기본값.
// 🔴 병합이 **얕다**. options 에 modules 를 주면 여기 toolbar 는 통째로 대체된다.
//    그래서 실제로 살아남는 건 boundary·readOnly 뿐인데, boundary 가 빠지면
//    링크 툴팁이 화면 밖으로 밀려 나간다. 지우지 말 것.
const DEFAULT_OPTIONS = {
  theme: 'snow',
  boundary: document.body,
  placeholder: 'Insert text here ...',
  readOnly: false,
}

export default {
  name: 'quill-editor',
  props: {
    content: String,
    value: String,
    disabled: { type: Boolean, default: false },
    options: { type: Object, required: false, default: () => ({}) },
    globalOptions: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return { _content: '' }
  },
  mounted() {
    this.initialize()
  },
  beforeDestroy() {
    // 퀼 1.x 에는 destroy 가 없다. 참조만 끊는다(원본과 같다).
    this.quill = null
    delete this.quill
  },
  methods: {
    initialize() {
      if (!this.$el) return

      const opts = Object.assign({}, DEFAULT_OPTIONS, this.globalOptions, this.options)
      this.quill = new Quill(this.$refs.editor, opts)

      // 값을 넣는 동안 입력을 막는다(원본과 같은 순서).
      this.quill.enable(false)
      const initial = this.value || this.content
      if (initial) this.setHtml(initial)
      if (!this.disabled) this.quill.enable(true)

      this.quill.on('selection-change', (range) => {
        if (!range) this.$emit('blur', this.quill)
        else this.$emit('focus', this.quill)
      })

      this.quill.on('text-change', () => {
        // 🔴 원본은 `$refs.editor.children[0].innerHTML` 로 읽었다. snow 테마에서
        //    첫 자식이 .ql-editor 라 맞아떨어졌을 뿐이고, quill.root 가 정확하다.
        let html = this.quill.root.innerHTML
        if (html === '<p><br></p>') html = ''
        this._content = html
        this.$emit('input', html)
        this.$emit('change', { html, text: this.quill.getText(), quill: this.quill })
      })

      this.$emit('ready', this.quill)
    },

    // 값 주입. 🔴 **`quill.pasteHTML` 을 쓰지 않는다** — 퀼 2 에서 사라졌다.
    //    `clipboard.dangerouslyPasteHTML` 은 1·2 양쪽에 다 있다.
    setHtml(html) {
      this.quill.clipboard.dangerouslyPasteHTML(html)
    },

    // 🔴 **_content 비교가 되먹임을 끊는다.** pasteHTML → text-change → input →
    //    부모의 v-model → 여기 watch 로 돌아오는데, 값이 방금 우리가 낸 것과
    //    같으면 다시 pasteHTML 하지 않는다. 빼면 무한 루프가 된다.
    applyValue(newVal) {
      if (!this.quill) return
      if (newVal && newVal !== this._content) {
        this._content = newVal
        this.setHtml(newVal)
      } else if (!newVal) {
        this.quill.setText('')
      }
    },
  },
  watch: {
    content(newVal) { this.applyValue(newVal) },
    value(newVal) { this.applyValue(newVal) },
    disabled(newVal) { if (this.quill) this.quill.enable(!newVal) },
  },
}
</script>
